import React from 'react'

import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  TooltipItem,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import {
  endOfMonth,
  format,
  startOfMonth,
  startOfYear,
  subMonths,
} from 'date-fns'
import SecondaryButton from '../../components/Buttons/SecondaryButton'
import { useMediaQuery } from 'usehooks-ts'
import { Listbox } from '@headlessui/react'
import classNames from 'classnames'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { PeriodOption } from './types'
import Button from '../../components/Buttons/Button'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

export const options = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: function (context: TooltipItem<'line'>) {
          return (
            context.dataset.label + ': ' + context.parsed.y.toFixed(2) + ' kg'
          )
        },
      },
    },
  },
}

const periods = [
  {
    name: '3 months',
    value: {
      periodStart: subMonths(startOfMonth(new Date()), 3),
      periodEnd: new Date(),
    },
  },
  {
    name: '6 months',
    value: {
      periodStart: subMonths(startOfMonth(new Date()), 6),
      periodEnd: endOfMonth(new Date()),
    },
  },
  {
    name: '12 months',
    value: {
      periodStart: subMonths(startOfMonth(new Date()), 12),
      periodEnd: endOfMonth(new Date()),
    },
  },
  {
    name: 'YTD',
    value: {
      periodStart: startOfYear(new Date()),
      periodEnd: endOfMonth(new Date()),
    },
  },
  { name: 'Max', value: undefined },
]

interface Co2ChartProps {
  period: PeriodOption
  monthlyReductions: number[]
  onPeriodChange: (period: PeriodOption) => void
  onExport: () => void
}

export const Co2Chart: React.FC<Co2ChartProps> = ({
  period,
  monthlyReductions,
  onPeriodChange,
  onExport,
}) => {
  const labels = monthlyReductions
    .map((_, i) => subMonths(new Date(), i))
    .map(d => format(d, 'MMM yyyy'))
    .reverse()

  const data = {
    labels,
    datasets: [
      {
        label: 'CO₂ reduction',
        data: monthlyReductions,
        borderColor: '#63CD88',
        backgroundColor: '#63CD88',
      },
    ],
  }

  const selectedPeriod = periods.find(p => p.value === period)

  const mobile = useMediaQuery('(max-width: 640px)')

  return (
    <div className={'mt-12'}>
      <div className="flex mb-4 justify-between items-center">
        <h3 className={'font-semibold'}>CO₂ reduction over time</h3>
        <div className={'flex gap-2'}>
          {mobile ? (
            <>
              <Listbox
                value={selectedPeriod}
                onChange={period => onPeriodChange(period.value)}
              >
                <div className={'relative'}>
                  <Listbox.Button
                    className={
                      'relative rounded-lg bg-white py-2 pl-3 pr-10 text-left border-gray border focus:outline-none'
                    }
                  >
                    <span className="block truncate">
                      {selectedPeriod?.name ?? 'Select periodicity'}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  </Listbox.Button>
                  <Listbox.Options
                    className={
                      'absolute mt-1 max-h-60 w-[175px] right-0 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm'
                    }
                  >
                    {periods.map(period => (
                      <Listbox.Option
                        key={period.value?.toString()}
                        value={period}
                        className={({ active }) =>
                          classNames(
                            'relative cursor-default select-none py-2 pl-10 pr-4',
                            {
                              'bg-emerald-50 text-emerald-900': active,
                              'text-gray-900': !active,
                            }
                          )
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={'block truncate'}>
                              {period.name}
                            </span>
                            {selected && (
                              <span
                                className={
                                  'absolute inset-y-0 left-0 flex items-center pl-3'
                                }
                              >
                                <CheckIcon
                                  className={'h-5 w-5 text-emerald-500'}
                                  aria-hidden={'true'}
                                />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </div>
              </Listbox>
              <Button className={'bg-emerald-500'} onClick={onExport}>
                <ArrowDownTrayIcon className={'h-4 w-4 text-white'} />
              </Button>
            </>
          ) : (
            <>
              <SecondaryButton
                onClick={() =>
                  onPeriodChange({
                    periodStart: subMonths(startOfMonth(new Date()), 3),
                    periodEnd: endOfMonth(new Date()),
                  })
                }
              >
                3 months
              </SecondaryButton>
              <SecondaryButton
                onClick={() =>
                  onPeriodChange({
                    periodStart: subMonths(startOfMonth(new Date()), 6),
                    periodEnd: endOfMonth(new Date()),
                  })
                }
              >
                6 months
              </SecondaryButton>
              <SecondaryButton
                onClick={() =>
                  onPeriodChange({
                    periodStart: subMonths(startOfMonth(new Date()), 12),
                    periodEnd: endOfMonth(new Date()),
                  })
                }
              >
                12 months
              </SecondaryButton>
              <SecondaryButton
                onClick={() =>
                  onPeriodChange({
                    periodStart: startOfYear(new Date()),
                    periodEnd: endOfMonth(new Date()),
                  })
                }
              >
                YTD
              </SecondaryButton>
              <SecondaryButton onClick={() => onPeriodChange(undefined)}>
                Max
              </SecondaryButton>
              <Button
                className={'flex gap-2 ml-4 bg-emerald-500'}
                onClick={onExport}
              >
                Export
                <ArrowDownTrayIcon className={'h-4 w-4 text-white'} />
              </Button>
            </>
          )}
        </div>
      </div>
      <Line data={data} options={options} />
    </div>
  )
}
